<template>
  <div class="p-6 bg-slate-50 min-h-screen">

    <!-- 1. Organization Header Card -->
    <div class="w-full mb-8">
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">

        <!-- Setup Banner -->
        <div
          class="w-full px-6 py-3 flex items-center justify-between cursor-pointer transition-colors border-b"
          :class="isOnboardingComplete
            ? 'bg-green-50 border-green-100 hover:bg-green-100 text-green-800'
            : 'bg-blue-50 border-blue-100 hover:bg-blue-100 text-blue-800'"
          @click="goToOnboarding"
        >
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full"
              :class="isOnboardingComplete ? 'bg-green-200 text-green-700' : 'bg-blue-200 text-blue-700'">
              <feather-icon :icon="isOnboardingComplete ? 'CheckIcon' : 'InfoIcon'" class="w-4 h-4" />
            </div>

            <div class="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <span class="font-bold">
                {{ isOnboardingComplete ? $t('orgDashboard.organization.allSet') : $t('orgDashboard.organization.stepInProgress') }}
              </span>
              <span class="text-sm md:text-base opacity-90">
                {{ isOnboardingComplete
                  ? $t('orgDashboard.organization.completeProg')
                  : $t('orgDashboard.organization.incompleteProg', { onboardingStep })
                }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-1 text-sm font-bold whitespace-nowrap pl-4">
            <span>{{ isOnboardingComplete ? $t('orgDashboard.organization.viewDetails') : $t('orgDashboard.organization.continueSetup') }}</span>
            <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
          </div>
        </div>

        <!-- Card Content -->
        <!-- Card Content -->
<div class="p-6 md:p-8 relative">
  <div
    v-if="orgData"
    class="flex flex-col lg:flex-row items-center lg:items-center gap-8 z-10 relative"
  >

    <!-- Left: Organization Info -->
    <div class="flex items-center gap-6 flex-1 min-w-0">

      <!-- Logo -->
      <div class="flex-shrink-0">
        <vs-avatar
          v-if="orgData.logo"
          class="shadow-md ring-4 ring-gray-50"
          size="140px"
          :src="constants.s3Bucket + '/organization/logo/' + orgData.logo"
        />

        <vs-avatar
          v-else
          class="shadow-md ring-4 ring-gray-50"
          size="140px"
          :src="require('@/assets/images/icons/organization/business_black_36dp.svg')"
        />
      </div>

      <!-- Info -->
      <div class="flex flex-col items-start gap-2 min-w-0">

        <div class="flex items-center gap-2 text-gray-500 font-medium mb-1">
          
          <span>{{ $t('orgDashboard.organization.welcomeBack') }}</span>
        </div>

        <h1 class="text-4xl font-bold text-gray-800 tracking-tight">
          {{ orgData.name }}
        </h1>

        <div class="flex items-center gap-4 text-gray-500 mt-1 flex-wrap">

          <div class="flex items-center gap-2">
            <feather-icon icon="GlobeIcon" class="w-4 h-4" />

            <a
              v-if="orgData.website"
              :href="formattedWebsiteUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="hover:text-blue-600 transition-colors font-medium"
            >
              {{ orgData.website }}
            </a>

            <span
              v-else
              class="text-gray-400 cursor-not-allowed"
            >
              {{ $t('orgDashboard.organization.addWebsiteLink') }}
            </span>
          </div>

          <div
            v-if="orgData.website"
            class="h-4 w-px bg-gray-300"
          ></div>

          <div
            v-if="orgData.website"
            class="cursor-pointer hover:text-gray-800 flex items-center gap-1 group"
            @click.stop="copyText"
          >
            <feather-icon
              :icon="isTextCopied ? 'CheckIcon' : 'CopyIcon'"
              class="w-4 h-4 group-hover:scale-110 transition-transform"
            />

            <span class="text-sm font-medium">
              {{ isTextCopied
                ? $t('orgDashboard.organization.linkCopied')
                : $t('orgDashboard.organization.copyLink')
              }}
            </span>
          </div>

        </div>
      </div>
    </div>

    <!-- Right: Key Roles + Talent Pool -->
    <div class="flex flex-row gap-5 flex-shrink-0">

  <!-- Talent Pool -->
  <div 
  class="bg-white border border-gray-200 rounded-xl px-6 py-4 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
  style="width: 320px; min-width: 320px; flex: 0 0 320px;"
  @click="goToTalentPool" 
>
    <div class="flex items-center justify-between">

      <!-- Left -->
      <div class="flex flex-col">
        <p class="text-gray-500 text-xs font-medium uppercase tracking-wider">
          {{ $t('orgDashboard.talentPool.title') }}
        </p>

        <h3 class="text-2xl text-left font-bold text-gray-800 mt-1">
          {{ metrics.talentPool }}
        </h3>
      </div>

      <!-- Right -->
      <div 
        class="p-2.5 bg-blue-50 rounded-lg text-blue-600 flex items-center justify-center h-10 w-10 flex-shrink-0"
      >
        <feather-icon 
          icon="UsersIcon" 
          class="w-5 h-5" 
        />
      </div>

    </div>

    <div class="text-xs text-blue-600 font-medium flex items-center gap-1 mt-3">
      {{ $t('orgDashboard.organization.viewCandidates') }}

      <feather-icon 
        icon="ArrowRightIcon" 
        class="w-3 h-3" 
      />
    </div>
  </div>


  <!-- Key Roles -->
  <div 
  class="bg-white border border-gray-200 rounded-xl px-6 py-4 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
  style="width: 320px; min-width: 320px; flex: 0 0 320px;"
  @click="goToOrgPlan" 
  >
    <div class="flex items-center justify-between">

      <!-- Left -->
      <div class="flex flex-col">
        <p class="text-gray-500 text-xs font-medium uppercase tracking-wider">
          {{ $t('orgDashboard.organization.keyRoles') }}
        </p>

        <h3 class="text-2xl text-left font-bold text-gray-800 mt-1">
          {{ metrics.keyRoles }}
        </h3>
      </div>

      <!-- Right -->
      <div 
        class="p-2.5 bg-purple-50 rounded-lg text-purple-600 flex items-center justify-center h-10 w-10 flex-shrink-0"
      >
        <feather-icon 
          icon="BriefcaseIcon" 
          class="w-5 h-5" 
        />
      </div>

    </div>

    <div class="text-xs text-left text-purple-600 font-medium mt-3">
      {{ $t('orgDashboard.organization.accrossOrganization') }}
    </div>
  </div>

</div>

  </div>

  <div
    class="absolute top-0 right-0 -mt-4 -mr-4 w-64 h-64 bg-gradient-to-bl from-blue-50 to-transparent rounded-full opacity-50 pointer-events-none"
  ></div>
</div>
      </div>
    </div>

    

    <!-- 3. Insights Section -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

      <!-- Organization Information -->
<div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">

  <!-- Main Content -->
  <div class="p-6 flex-1">

    <!-- Header -->
    <h3 class="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
      <feather-icon
        icon="InfoIcon"
        class="w-5 h-5 text-blue-500"
      />
      Organization Information
    </h3>

    <!-- About -->
    <div>
      <p class="text-large font-semibold text-gray-400 uppercase tracking-wider mb-2">
        About Organization
      </p>

      <p
        v-if="organizationDetails.about"
        class="text-medium text-gray-600 leading-6 text-left"
      >
        {{ organizationDetails.about }}
      </p>

      <p
        v-else
        class="text-medium text-gray-400 italic text-left"
      >
        No organization description available.
      </p>
    </div>

  </div>


  <!-- Bottom Footer -->
  <div class="border-t border-gray-100 bg-gray-50/60 px-6 py-4">

    <div class="grid grid-cols-2 gap-8">

      <!-- Organization Type -->
      <div class="flex items-center gap-3 justify-start">

        <div
          class="w-9 h-9 rounded-lg bg-purple-50 text-purple-500 flex items-center justify-center flex-shrink-0"
        >
          <feather-icon
            icon="BriefcaseIcon"
            class="w-4 h-4"
          />
        </div>

        <div class="text-left">
          <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Organization Type
          </p>

          <span
            v-if="organizationDetails.organizationType"
            class="text-sm font-medium text-gray-700"
          >
            {{ organizationDetails.organizationType }}
          </span>

          <span
            v-else
            class="text-sm text-gray-400 italic"
          >
            Not provided
          </span>
        </div>

      </div>


      <!-- Location -->
      <div class="flex items-center gap-3 justify-start">

        <div
          class="w-9 h-9 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center flex-shrink-0"
        >
          <feather-icon
            icon="MapPinIcon"
            class="w-4 h-4"
          />
        </div>

        <div class="text-left">
          <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Location
          </p>

          <span
            v-if="organizationDetails.location"
            class="text-sm font-medium text-gray-700"
          >
            {{ organizationDetails.location }}
          </span>

          <span
            v-else
            class="text-sm text-gray-400 italic"
          >
            Not provided
          </span>
        </div>

      </div>

    </div>

  </div>

</div>

      <!-- Plans Velocity & Categories Insight -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">

  <!-- Active Plans & Categories -->
  <div>
    <h3 class="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
      <feather-icon 
        icon="ActivityIcon" 
        class="w-5 h-5 text-gray-400" 
      />
      Active Plans
    </h3>

    <p class="text-gray-500 text-sm mb-6">
      Distribution of active plans by plan type.
    </p>

    <!-- Summary -->
    <div class="flex items-center justify-between mb-5">

      <!-- Active -->
      <div class="flex items-center gap-4">
        <div class="flex items-center justify-center">
          <feather-icon 
            icon="LayersIcon" 
            class="w-7 h-7 text-blue-500" 
          />
        </div>

        <div>
          <div class="flex items-baseline gap-2">
            <span class="text-3xl font-bold text-gray-800">
              {{ metrics.activePlans }}
            </span>

            <span class="text-xs uppercase tracking-wide text-gray-500 font-bold">
              Active
            </span>
          </div>
        </div>
      </div>

      <!-- Completed -->
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
          <feather-icon 
            icon="CheckCircleIcon" 
            class="w-7 h-7 text-green-400" 
          />
        </div>

        <div class="text-right">
          <div class="flex items-baseline justify-end gap-2">
            <span class="text-2xl font-bold text-gray-500">
              {{ metrics.completedPlans }}
            </span>
            <span class="text-xs uppercase tracking-wide text-gray-400 font-bold">
              Completed
            </span>
          </div>
        </div>
      </div>

    </div>

    <!-- Category Label -->
    <div class="flex items-center justify-between mb-2">
      <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        Active plan types
      </span>

      <span class="text-[11px] text-gray-400">
        {{ metrics.activePlans }} plans
      </span>
    </div>

    <!-- Active Plan Types -->
<div class="mt-3">

  <!-- <div class="flex items-center justify-between mb-2">
    

    <span class="text-[11px] text-gray-400">
      {{ metrics.activePlans }} plans
    </span>
  </div> -->

  <!-- Bar + Dynamic Category Labels -->
  <div class="relative">

    <!-- Colored Bar -->
    <div class="w-full bg-gray-100 rounded-full h-3 overflow-hidden flex">
      <div
        v-for="cat in planCategories"
        :key="cat.name"
        :class="[
          cat.color,
          'h-full transition-all duration-700'
        ]"
        :style="{ width: cat.percent + '%' }"
        :title="cat.name + ': ' + cat.count"
      >
      </div>
    </div>

    <!-- Labels positioned under their respective color -->
    <div class="relative w-full h-7">

      <div
        v-for="(cat, index) in planCategories"
        :key="cat.name"
        class="absolute top-2 flex items-center gap-3 whitespace-nowrap"
        :style="{
          left: getCategoryLabelPosition(index) + '%',
          transform: 'translateX(-50%)'
        }"
      >

        <span
          class="w-2 h-2 rounded-full flex-shrink-0"
          :class="cat.color"
        ></span>

        <span class="text-sm text-gray-600 font-medium">
          {{ cat.name }}:
        </span>

        <span class="text-sm font-bold text-gray-800">
          {{ cat.count }}
        </span>

      </div>

    </div>

  </div>

</div>

    <!-- Category Legend -->
    <!-- <div class="flex flex-wrap gap-x-5 gap-y-2">

      <div
        v-for="cat in planCategories"
        :key="cat.name"
        class="flex items-center gap-2"
      >
        <span
          class="w-2.5 h-2.5 rounded-full"
          :class="cat.color"
        ></span>

        <span class="text-xs text-gray-600 font-medium">
          {{ cat.name }}
        </span>

        <span class="text-xs font-bold text-gray-800">
          {{ cat.count }}
        </span>
      </div>

    </div> -->
  </div>


  <!-- Top Active Frameworks -->
  <div class="mt-6 pt-5 border-t border-gray-100">

    <div class="flex justify-between items-center mb-3">

      <span class="text-medium font-bold text-gray-800">
        Top Active Frameworks
      </span>

      <span
        class="text-xs text-blue-600 font-semibold cursor-pointer hover:underline flex items-center gap-1"
        @click="goToBoards"
      >
        View All
        <feather-icon
          icon="ArrowRightIcon"
          class="w-3 h-3"
        />
      </span>

    </div>

    <div v-if="isLoadingPlans" class="flex justify-center py-4">
      <feather-icon
        icon="RefreshCwIcon"
        class="w-5 h-5 text-blue-500 animate-spin"
      />
    </div>

    <div
      v-else-if="coachPlans.length === 0"
      class="text-center text-xs text-gray-400 py-2"
    >
      No active framework plans found.
    </div>

    <div v-else class="space-y-2">

      <div
        v-for="plan in coachPlans.slice(0, 3)"
        :key="plan.id"
        class="flex justify-between items-center p-2.5 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-200 cursor-pointer group"
        @click="goToBoards"
      >

        <!-- Plan Info -->
        <div class="flex flex-col items-start text-left w-full overflow-hidden pr-2">

          <span
            class="text-medium font-bold text-gray-700 group-hover:text-blue-700 transition-colors truncate w-full"
          >
            {{ plan.title }}
          </span>

          <span class="text-[10px] text-gray-500 font-medium uppercase mt-0.5">
            {{ plan.framework_id }}
          </span>

        </div>

        <!-- Metrics -->
        <div class="flex items-center gap-2 flex-shrink-0">

          <!-- Users -->
          <div
            class="flex items-center gap-1 text-md font-semibold text-gray-600 bg-white px-2 py-1 rounded shadow-sm border border-gray-100"
            title="Enrolled Users"
          >
            <feather-icon
              icon="UsersIcon"
              class="w-3.5 h-3.5 text-blue-500"
            />
            {{ plan.assigned_users ? plan.assigned_users.length : 0 }}
          </div>

          <!-- Credits -->
          <div
            class="flex items-center gap-1 text-md font-semibold text-gray-600 bg-white px-2 py-1 rounded shadow-sm border border-gray-100"
            title="Credits"
          >
            <feather-icon
              icon="DollarSignIcon"
              class="w-3.5 h-3.5 text-orange-400"
            />
            {{ plan.total_credits }}
          </div>

        </div>

      </div>

    </div>

  </div>

</div>
    </div>

    <!-- 4. Quick Links Footer -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <!-- KVDR Reports Card -->
        <div class="bg-white border border-gray-200 rounded-xl p-6 text-gray-800 shadow-sm cursor-pointer transform transition hover:-translate-y-1 hover:shadow-md" @click="goToReports">
          <div class="flex items-center justify-between mb-4">
             <div class="bg-blue-50 p-2 rounded-lg text-blue-600 flex items-center justify-center h-10 w-10">
                <feather-icon icon="PieChartIcon" class="w-6 h-6" />
             </div>
             <feather-icon icon="ArrowRightIcon" class="w-5 h-5 text-gray-400" />
          </div>
          <h4 class="text-xl font-bold mb-1">{{ $t('orgDashboard.organization.kvdrReports') }}</h4>
          <p class="text-gray-500 text-sm">{{ $t('orgDashboard.organization.kvdrReports_desc') }}</p>
        </div>

        <!-- Talent Explorer Card -->
        <div class="bg-white border border-gray-200 rounded-xl p-6 text-gray-800 shadow-sm cursor-pointer transform transition hover:-translate-y-1 hover:shadow-md" @click="goToTalentPool">
           <div class="flex items-center justify-between mb-4">
             <div class="bg-purple-50 p-2 rounded-lg text-purple-600 flex items-center justify-center h-10 w-10">
                <feather-icon icon="UsersIcon" class="w-6 h-6" />
             </div>
             <feather-icon icon="ArrowRightIcon" class="w-5 h-5 text-gray-400" />
          </div>
          <h4 class="text-xl font-bold mb-1">{{ $t('orgDashboard.organization.talentExplorer') }}</h4>
          <p class="text-gray-500 text-sm">{{ $t('orgDashboard.organization.talentExplorer_desc') }}</p>
        </div>
    </div>

  </div>
</template>

<script>
import constants from "@/constants.js";

export default {
  data() {
    return {
      constants,
      onboardingStep: 1,
      isTextCopied: false,
      metrics: {
        talentPool: 0,
        keyRoles: 0,
        activePlans: 0,
        completedPlans: 0,
      },
      topRoles: [],
      // --- NEW DATA STATES ---
      coachPlans: [],
      isLoadingPlans: false,
      organizationDetails: {
      about: '',
      organizationType: '',
      location: ''
    },
    };
  },
  computed: {
    orgData() {
      return this.$store.state.organization.orgData;
    },
    formattedWebsiteUrl() {
      const url = this.orgData?.website;
      if (!url) return '#';
      if (url.match(/^https?:\/\//)) {
        return url;
      }
      return `https://${url}`;
    },
    isOnboardingComplete() {
      return this.onboardingStep > 4;
    },
    planVelocityPercentage() {
      const total = this.metrics.activePlans + this.metrics.completedPlans;
      if (total === 0) return 50;
      return (this.metrics.activePlans / total) * 100;
    },
    planCategories() {
      const boards = this.$store.state.boards.allBoards || [];
      let ecosystem = 0, fitment = 0, development = 0, succession = 0;
      
      // Categorize active boards dynamically
      boards.forEach(b => {
        const title = (b.title || '').toLowerCase();
        
        if (title.includes('ecosystem') || title.includes('invit')) {
            ecosystem++;
        } else if (title.includes('fitment') || title.includes('onboard')) {
            fitment++;
        } else if (title.includes('development') || title.includes('learn') || title.includes('performance')) {
            development++;
        } else {
            // Coach plans & anything else falls under Succession
            succession++;
        }
      });
      
      const total = boards.length || 1; // Fallback to 1 to prevent divide by zero
      
      // Map matching your image (Ecosystem=Blue, Fitment=Yellow, Development=Purple, Succession=Green)
      const categories = [
        { name: 'Ecosystem', count: ecosystem, percent: (ecosystem/total)*100, color: 'bg-blue-500' },
        { name: 'Fitment', count: fitment, percent: (fitment/total)*100, color: 'bg-yellow-500' },
        { name: 'Development', count: development, percent: (development/total)*100, color: 'bg-purple-500' },
        { name: 'Succession', count: succession, percent: (succession/total)*100, color: 'bg-green-500' }
      ];
      
      // Only return categories that actually have plans in them
      return categories.filter(c => c.count > 0);
    }
  },
  watch: {
    orgData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && newVal._id) {
          this.initDashboardData(newVal._id);
        }
        if (newVal && newVal.slug) {
          this.loadOnboardingStep();
        }
      }
    },
    '$store.state.organization.employees': {
      handler() { this.calculateRoleMetrics(); },
      deep: true
    }
  },
  methods: {
    getCategoryLabelPosition(index) {
  let position = 0

  for (let i = 0; i < index; i++) {
    position += this.planCategories[i].percent
  }

  return position + (this.planCategories[index].percent / 2)
},
    initDashboardData(orgId) {
      this.fetchBoardData(orgId);
      this.$store.dispatch("organization/fetchAllEmployees", { organizationId: orgId });
      this.fetchTotalOrgRoles(orgId);
      this.fetchCoachPlans(orgId);

      // Fetch organization information
      this.fetchOrganizationDetails(orgId);
    },

    // --- NEW: Fetch Coach/Framework Plans ---
    async fetchCoachPlans(orgId) {
      this.isLoadingPlans = true;
      try {
        const response = await fetch(`https://parser.ai.getboarded.com/api/v2/coach-plans/${orgId}`);
        const result = await response.json();
        
        if (result.status === 'success' && result.data) {
          // Store data and sort so plans with most users show first
          this.coachPlans = result.data.sort((a, b) => {
            const usersA = a.assigned_users ? a.assigned_users.length : 0;
            const usersB = b.assigned_users ? b.assigned_users.length : 0;
            return usersB - usersA;
          });
        }
      } catch (err) {
        console.error("Failed to fetch coach plans:", err);
      } finally {
        this.isLoadingPlans = false;
      }
    },
    async fetchOrganizationDetails(orgId) {
  try {
    const token =
      localStorage.getItem('feathers-jwt') ||
      localStorage.getItem('accessToken');

    const response = await fetch(
      `https://api.getboarded.tech/organization/${orgId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch organization details: ${response.status}`
      );
    }

    const result = await response.json();

    console.log('Organization API response:', result);

    const data = result.data || result.organization || result;

    this.organizationDetails = {
      about: data.about || '',
      organizationType: data.organizationType || '',
      location: data.location || ''
    };

  } catch (error) {
    console.error(
      'Failed to fetch organization details:',
      error
    );

    this.organizationDetails = {
      about: '',
      organizationType: '',
      location: ''
    };
  }
},

    async fetchTotalOrgRoles(orgId) {
      try {
        const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
        let allRoles = [];

        const res1 = await fetch(`https://api.getboarded.tech/kvdr?orgId=${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res1.ok) {
          const data1 = await res1.json();
          const content = data1.data || data1;
          
          if (content && typeof content === 'object' && !Array.isArray(content) && content.kvdrRoles) {
            allRoles = content.kvdrRoles;
          } else if (Array.isArray(content)) {
            content.forEach(container => {
              if (container && container.kvdrRoles) {
                allRoles = allRoles.concat(container.kvdrRoles);
              }
            });
          }
        }

        if (allRoles.length === 0) {
          const res2 = await fetch(`https://api.getboarded.tech/kvdr/${orgId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (res2.ok) {
            const data2 = await res2.json();
            const retryContent = data2.data || data2;
            
            if (retryContent && typeof retryContent === 'object' && !Array.isArray(retryContent) && retryContent.kvdrRoles) {
              allRoles = retryContent.kvdrRoles;
            } else if (Array.isArray(retryContent)) {
              retryContent.forEach(container => {
                if (container && container.kvdrRoles) {
                  allRoles = allRoles.concat(container.kvdrRoles);
                }
              });
            }
          }
        }
        this.metrics.keyRoles = allRoles.length;

      } catch (err) {
        console.error("Failed to fetch total roles:", err);
      }
    },

    fetchBoardData(orgId) {
      const promises = [
        this.$store.dispatch("boards/fetchAllBoards", orgId),
        this.$store.dispatch("boards/fetchAllArchivedBoards", orgId)
      ];
      Promise.all(promises).then(() => {
        this.metrics.activePlans = this.$store.state.boards.allBoards.length;
        this.metrics.completedPlans = this.$store.state.boards.allArchivedBoards.length;
      }).catch(console.error);
    },

    calculateRoleMetrics() {
      const allEmployees = this.$store.state.organization.employees || [];
      
      if (allEmployees.length === 0) {
        return;
      }
      this.metrics.talentPool = allEmployees.length;

      const roleCounts = {};
      let unassignedCount = 0;

      allEmployees.forEach(emp => {
        const roleName = emp.roleTitle || emp.roleName || emp.designation;
        if (roleName && typeof roleName === 'string' && roleName.trim() !== '') {
          const cleanName = roleName.trim();
          roleCounts[cleanName] = (roleCounts[cleanName] || 0) + 1;
        } else {
          unassignedCount++;
        }
      });

      let distribution = Object.entries(roleCounts).map(([title, count]) => ({ title, count }));
      if (unassignedCount > 0) {
        distribution.push({ title: 'Unassigned', count: unassignedCount });
      }

      distribution.sort((a, b) => b.count - a.count);
      this.topRoles = distribution.slice(0, 5);
    },

    goToOnboarding() {
      if (this.orgData && this.orgData.slug) {
        this.$router.push(`/organization/${this.orgData.slug}/onboarding`);
      }
    },
    goToTalentPool() {
      if (this.orgData && this.orgData.slug) {
        this.$router.push(`/organization/${this.orgData.slug}/talent`);
      }
    },
    goToReports() {
      if (this.orgData && this.orgData.slug) {
        this.$router.push(`/organization/${this.orgData.slug}/reports`);
      }
    },
    goToBoards() {
      if (this.orgData && this.orgData.slug) {
       this.$router.push(`/organization/${this.orgData.slug}/boards`);
      }
    },
    goToOrgPlan() {
      if (this.orgData && this.orgData.slug) {
        this.$router.push(`/organization/${this.orgData.slug}/roles`);
      }
    },
    async copyText() {
      try {
        await navigator.clipboard.writeText(this.orgData.website);
        this.isTextCopied = true;
        setTimeout(() => (this.isTextCopied = false), 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    },
    loadOnboardingStep() {
      if (this.orgData && this.orgData.slug) {
        const key = `onboardingStep_${this.orgData.slug}`;
        const savedStep = localStorage.getItem(key);
        this.onboardingStep = savedStep ? parseInt(savedStep) : 1;
      }
    }
  }
};
</script>

<style scoped>
.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Custom Scrollbar for inner elements to keep UI clean */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>